import type { BillingPlan } from '@/lib/billing/types'

/**
 * Pure billing-recurrence label helpers for the Plan card. Kept out of the page
 * component so they can be unit-tested without pulling the page's client/server
 * dependencies (server actions, supabase).
 */

function getPlanRecurringAmountCents(
  plan: BillingPlan,
  interval: string,
  count: number,
): number {
  const matched = plan.billing_intervals?.find(
    (i) => i.interval === interval && i.interval_count === count,
  )
  if (matched?.stripe_price_amount_cents != null) return matched.stripe_price_amount_cents
  // Fallback: naive monthly × cycle length. Misses plan-level discount but
  // only reached when stripe_price_amount_cents hasn't been cached yet.
  const monthsInCycle = interval === 'month' ? count : interval === 'year' ? count * 12 : count
  return plan.price_monthly_cents * monthsInCycle
}

function formatIntervalSuffix(interval: string, count: number): string {
  if (interval === 'month') {
    if (count === 1) return '/mo'
    if (count === 3) return '/quarter'
    if (count === 6) return '/6mo'
    return `/${count}mo`
  }
  if (interval === 'year') {
    if (count === 1) return '/yr'
    return `/${count}yr`
  }
  return '/cycle'
}

/**
 * Secondary line under the plan name on the Plan card — surfaces the recurring
 * billing info ("$30/mo · next charge May 28"). Returns null when there's nothing
 * useful to say (e.g. a Platform-managed plan with no period_end on file).
 *
 * `timezone` (viewer IANA tz, default 'UTC') is threaded explicitly so the helper
 * stays pure and boundary timestamps don't silently shift a day.
 */
export function renderPlanRecurrenceLine(
  subscription: {
    payment_provider: string
    billing_interval: string
    billing_interval_count: number
    current_period_end: string | null
    cancel_at_period_end: boolean
    pending_plan_change_initiated_at: string | null
  },
  plan: BillingPlan,
  timezone: string = 'UTC',
): string | null {
  const dateLabel = formatPeriodDate(subscription.current_period_end, timezone)

  if (subscription.pending_plan_change_initiated_at) {
    return 'Plan change pending — awaiting payment'
  }
  if (subscription.cancel_at_period_end) {
    return dateLabel ? `Cancels on ${dateLabel}` : 'Cancels at period end'
  }
  if (subscription.payment_provider === 'manual') {
    return dateLabel ? `Renews ${dateLabel}` : null
  }
  if (plan.price_monthly_cents === 0) {
    return 'Free plan · no recurring charge'
  }
  const cents = getPlanRecurringAmountCents(
    plan,
    subscription.billing_interval,
    subscription.billing_interval_count,
  )
  const amount = `$${(cents / 100).toFixed(2)}`
  const suffix = formatIntervalSuffix(subscription.billing_interval, subscription.billing_interval_count)
  // Shopify surfaces the SAME amount as Stripe — only the wording differs (renews
  // via Shopify vs next charge), since billing is settled by Shopify, not a card.
  if (subscription.payment_provider === 'shopify') {
    return dateLabel ? `${amount}${suffix} · renews ${dateLabel} via Shopify` : `${amount}${suffix} · via Shopify`
  }
  return dateLabel ? `${amount}${suffix} · next charge ${dateLabel}` : `${amount}${suffix}`
}

/** A billing-period boundary as the Plan card shows it ("May 28"), in the viewer's time zone. */
export function formatPeriodDate(iso: string | null, timezone: string = 'UTC'): string | null {
  return iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: timezone }) : null
}
